/**
 * Add-ons on the product page (docs/addons.md, the contract; docs/history/2026-09-27-addons-v4-spec.md, section 3).
 *
 * ck-addons.liquid renders the block with the line's collection path and the cover's handle.
 * This file:
 *   1. fetches that collection's add-ons whenever the vehicle changes, one JSON page at a
 *      time through the Section Rendering API, asking the store to narrow them to the vehicle
 *      and the cover (filters that only take effect where the Search and Discovery app defines
 *      them), then tests every rule again itself, so a missing filter costs bytes, never
 *      correctness. Pages are cached on success; the latest vehicle wins; a failed fetch
 *      shows nothing rather than something wrong, and lets the same vehicle try again.
 *   2. builds one card per add-on group that fits, and opens the dialog, cloned from the
 *      <template> in the snippet, where each option is narrowed by the choice before it.
 *   3. exposes CK.addons.selection(), which the configurator reads at Add to cart.
 * The vehicle is the configurator's own, read through CK.getVehicle(); nothing is written
 * anywhere. Selection lives on the card node as data attributes and is cleared the moment
 * the year, make or model changes, before the new fetch starts.
 */
(function () {
  if (!window.CK || !window.HTMLDialogElement || CK.addons) return; // once per page, whatever loads the asset again

  var TEXT_PATTERN = "[A-Za-z0-9 #'.\\-]*"; // the text field's HTML pattern: letters, digits, space # ' . -
  var FILTERS = ['addon_makes', 'addon_models', 'addon_fabrics']; // the store's filters, by metafield key
  var pages = {};              // url -> promise of one page's JSON, kept on success only
  var latest = 0;              // the fetch sequence: a response from an older vehicle is dropped
  var members = new WeakMap(); // card -> the group's add-ons that fit the current vehicle
  var activeCard = null;
  var activeDialog = null;

  /* ---------- helpers ---------- */

  function root() { return document.querySelector('[data-ck-addons]'); }

  function make(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function radio(name, value) {
    return Object.assign(make('input'), { type: 'radio', name: name, value: value, required: true });
  }

  function fire() {
    document.dispatchEvent(new CustomEvent('ck:addons-changed'));
  }

  function norm(s) { return String(s || '').trim().toLowerCase(); }

  // A description comes out of the product's HTML with its entities intact: decode them once, as text.
  function decode(html) {
    var t = document.createElement('textarea');
    t.innerHTML = html || '';
    return t.value;
  }

  // The lowest-priced available variant across the given add-ons: the "from" price on a card.
  function cheapest(list) {
    var best = null;
    list.forEach(function (a) {
      a.variants.forEach(function (v) { if (v.available && (!best || v.price < best.price)) best = v; });
    });
    return best;
  }

  /* ---------- the fetch ---------- */

  // One page of the line's collection, narrowed to this vehicle and cover where the filters exist.
  function pageUrl(base, vehicle, cover, page) {
    var q = new URLSearchParams({ section_id: 'ck-addons-collection', page: page });
    [vehicle.make, vehicle.model, cover].forEach(function (value, i) {
      q.append('filter.p.m.custom.' + FILTERS[i], value);
      q.append('filter.p.m.custom.' + FILTERS[i], 'All');
    });
    return base + '?' + q;
  }

  function section(url) {
    if (!pages[url]) {
      pages[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(url + ' -> HTTP ' + r.status); return r.text(); })
        .then(function (html) {
          // The JSON sits between the section wrapper's tags; taken as text, no entity or "<" in it is touched.
          var data = JSON.parse(html.slice(html.indexOf('>') + 1, html.lastIndexOf('<')));
          data.addons.forEach(function (a) { a.description = decode(a.description); });
          return data;
        });
      pages[url].catch(function () { delete pages[url]; });
    }
    return pages[url];
  }

  // Every add-on of the collection for this vehicle, following the pages while `more` is true.
  function addonsFor(base, vehicle, cover, seq) {
    var all = [];
    function next(page) {
      if (seq !== latest) return Promise.resolve(all); // the vehicle changed: no more pages for this one
      return section(pageUrl(base, vehicle, cover, page)).then(function (data) {
        all = all.concat(data.addons);
        return data.more ? next(page + 1) : all;
      });
    }
    return next(1);
  }

  /* ---------- the rules, tested again in the browser ---------- */

  // A fit list is null or holds "All" for any value, else the values it allows.
  function oneOf(list, value) {
    if (!list || !list.length) return true;
    var v = norm(value);
    return list.some(function (x) { return norm(x) === 'all' || norm(x) === v; });
  }

  // A record without a group is not an add-on the app saved, whatever collection it sits in.
  function fits(addon, vehicle, cover) {
    var f = addon.fit;
    var year = Number(vehicle.year);
    return Boolean(addon.group)
      && oneOf(f.fabrics, cover)
      && oneOf(f.makes, vehicle.make)
      && oneOf(f.models, vehicle.model)
      && (!f.years[0] || year >= f.years[0])
      && (!f.years[1] || year <= f.years[1])
      && addon.variants.some(function (v) { return v.available; });
  }

  function eligible(card) { return members.get(card) || []; }

  /* ---------- cards ---------- */

  function paintImage(card, src) {
    var img = card.querySelector('img');
    img.hidden = !src;
    if (src) img.src = src;
  }

  // No selection: the face shows the group, its first image and its lowest price.
  function resetCard(card) {
    var list = eligible(card), from = cheapest(list);
    var mixed = from && list.some(function (a) { return a.variants.some(function (v) { return v.available && v.price !== from.price; }); });
    card.setAttribute('aria-pressed', 'false');
    ['id', 'price', 'member', 'text', 'text-label'].forEach(function (k) { card.removeAttribute('data-ck-addon-' + k); });
    paintImage(card, list.length && list[0].image);
    card.querySelector('.ck-addons__price').textContent = from ? (mixed ? 'From ' : '') + from.price_text : '';
    var sub = card.querySelector('.ck-addons__sub');
    sub.textContent = '';
    sub.hidden = true;
  }

  // Display order: the sort metafield, then the title; a group's card sits where its first add-on does.
  function bySort(a, b) {
    return (a.sort || 0) - (b.sort || 0) || String(a.title).localeCompare(String(b.title));
  }

  // One card per group, in display order.
  function buildCards(el, addons) {
    var list = el.querySelector('[data-ck-cards]');
    var template = document.querySelector('template[data-ck-addons-card]');
    var cards = Object.create(null); // a group may be named like an Object property
    list.innerHTML = '';
    addons.slice().sort(bySort).forEach(function (a) {
      var card = cards[a.group];
      if (!card) {
        card = cards[a.group] = template.content.firstElementChild.cloneNode(true);
        card.querySelector('.ck-addons__name').textContent = a.group;
        members.set(card, []);
        list.appendChild(card);
      }
      members.get(card).push(a);
    });
    list.querySelectorAll('.ck-addons__card').forEach(resetCard);
    el.hidden = !list.children.length;
    fire();
  }

  function applyVehicle() {
    var el = root();
    if (!el) return;
    var vehicle = CK.getVehicle();
    // The vehicle event also fires on a submodel change and on the base-vehicle back-fill.
    // Neither changes eligibility, so keep the customer's selection unless year, make or
    // model actually changed.
    var stamp = vehicle ? [vehicle.year, vehicle.make, vehicle.model].map(norm).join('|') : '';
    if (el.getAttribute('data-ck-stamp') === stamp) return;
    el.setAttribute('data-ck-stamp', stamp);
    teardown();
    buildCards(el, []); // the old selection is gone, and the configurator told, before anything is fetched
    var seq = ++latest;
    if (!vehicle) return;
    var cover = el.getAttribute('data-ck-cover');
    addonsFor(el.getAttribute('data-ck-collection'), vehicle, cover, seq).then(function (addons) {
      if (seq === latest && el.isConnected) buildCards(el, addons.filter(function (a) { return fits(a, vehicle, cover); }));
    }, function () {
      if (seq === latest) el.removeAttribute('data-ck-stamp'); // so the same vehicle tries again
    });
  }

  /* ---------- selection, read by the configurator ---------- */

  function selection() {
    var out = { lines: [], parentProperties: {}, totalCents: 0 };
    var el = root();
    if (!el) return out;
    var ids = [];
    el.querySelectorAll('.ck-addons__card[aria-pressed="true"]').forEach(function (card) {
      var id = Number(card.getAttribute('data-ck-addon-id'));
      var text = card.getAttribute('data-ck-addon-text');
      var line = { id: id, quantity: 1 };
      if (text) { line.properties = Object.create(null); line.properties[card.getAttribute('data-ck-addon-text-label')] = text; } // no prototype: any label is a plain key
      out.lines.push(line);
      ids.push(text ? id + ':' + text : String(id));
      out.totalCents += Number(card.getAttribute('data-ck-addon-price'));
    });
    // One signature per distinct add-on set, so two covers with different add-ons never merge.
    if (ids.length) out.parentProperties._AddonSet = ids.sort().join(',');
    return out;
  }

  CK.addons = { selection: selection };

  /* ---------- dialog ---------- */

  // Removes the open dialog, if any, and returns focus to its card.
  function teardown() {
    var dlg = activeDialog, card = activeCard;
    activeDialog = activeCard = null;
    if (!dlg) return;
    if (dlg.open) dlg.close();
    dlg.remove();
    if (card.isConnected) card.focus({ preventScroll: true });
  }

  function showError(form, message) {
    var err = form.querySelector('[data-ck-error]');
    err.textContent = message || '';
    err.hidden = !message;
  }

  function firstProblem(form) {
    var bad = Array.prototype.find.call(form.elements, function (el) { return el.validity && !el.validity.valid; }); // validity, not :invalid: the same answer in every engine
    if (!bad) return '';
    if (bad.type === 'radio') {
      var legend = bad.closest('fieldset').querySelector('legend').textContent;
      return legend === 'Choose one' ? 'Choose one option.' : 'Choose a ' + legend.toLowerCase() + '.';
    }
    if (bad.validity.patternMismatch) return 'Use letters, numbers, spaces, apostrophes, periods, hyphens or #.';
    return 'Enter ' + bad.closest('label').querySelector('.ck-addons__label').textContent.toLowerCase() + '.';
  }

  // "Enter up to 15 characters, then choose a font and thread color, then confirm."
  function instructionFor(addon) {
    var names = addon.options.map(function (o) { return String(o).toLowerCase(); });
    var steps = [addon.text && 'enter up to ' + addon.text.max + ' characters',
                 names.length && 'choose a ' + names.join(', ').replace(/, ([^,]*)$/, ' and $1'), 'confirm'].filter(Boolean);
    return steps.length > 1 ? steps.join(', then ').replace(/^./, function (c) { return c.toUpperCase(); }) + '.' : '';
  }

  function pickedValues(form, addon) {
    return addon.options.map(function (_, i) {
      var r = form.querySelector('input[name="ck_opt_' + i + '"]:checked');
      return r ? r.value : null;
    });
  }

  // The values option `idx` can still take: those of available variants that match every
  // earlier pick. This is what makes the options dependent.
  function valuesFor(addon, idx, picked) {
    var values = [];
    addon.variants.forEach(function (v) {
      if (!v.available) return;
      for (var j = 0; j < idx; j++) if (picked[j] && v.options[j] !== picked[j]) return;
      if (values.indexOf(v.options[idx]) < 0) values.push(v.options[idx]);
    });
    return values;
  }

  // The variant whose option values equal the picked ones; null until every option is picked
  // and only if that combination exists. Never guess: a guessed variant is a wrong SKU.
  function variantFor(addon, picked) {
    if (!addon.options.length) return addon.variants[0];
    return addon.variants.find(function (v) {
      return picked.every(function (val, j) { return v.options[j] === val; });
    }) || null;
  }

  function variantById(addon, id) {
    return addon.variants.find(function (v) { return String(v.id) === id; }) || null;
  }

  function currentAddon(form, list) {
    if (list.length === 1) return list[0];
    var r = form.querySelector('input[name="ck_member"]:checked');
    return r ? list.find(function (a) { return a.handle === r.value; }) || null : null;
  }

  function paintPrice(form, addon) {
    var v = addon ? variantFor(addon, pickedValues(form, addon)) : null;
    var shown = v || (addon && cheapest([addon]));
    form.querySelector('[data-ck-price]').textContent = shown ? '+' + shown.price_text : '';
    return v;
  }

  function renderMembers(form, list, state) {
    var zone = form.querySelector('[data-ck-members]');
    zone.innerHTML = '';
    zone.hidden = list.length < 2;
    if (zone.hidden) return;
    var fs = make('fieldset', 'ck-addons__choice');
    fs.appendChild(make('legend', 'ck-addons__label', 'Choose one'));
    list.forEach(function (a) {
      var opt = make('label', 'ck-addons__opt');
      var r = radio('ck_member', a.handle);
      if (a.handle === state.member) { r.checked = true; opt.classList.add('is-selected'); }
      opt.appendChild(r);
      if (a.image) opt.appendChild(Object.assign(make('img', 'ck-addons__opt-media'), { src: a.image, alt: '', width: 96, height: 54, loading: 'lazy' }));
      var copy = make('span', 'ck-addons__opt-copy');
      var head = make('span', 'ck-addons__opt-head');
      head.appendChild(make('span', 'ck-addons__opt-title', a.title));
      head.appendChild(make('span', 'ck-addons__opt-price', '+' + cheapest([a]).price_text));
      copy.appendChild(head);
      if (a.description) copy.appendChild(make('span', 'ck-addons__opt-desc', a.description));
      opt.appendChild(copy);
      fs.appendChild(opt);
    });
    zone.appendChild(fs);
  }

  // One option's chips, offering only the values the earlier picks leave open.
  function optionGroup(addon, idx, picked) {
    var fs = make('fieldset', 'ck-addons__choice');
    fs.setAttribute('data-ck-opt', idx);
    fs.appendChild(make('legend', 'ck-addons__label', addon.options[idx]));
    var wrap = make('div', 'ck-addons__chips');
    valuesFor(addon, idx, picked).forEach(function (val) {
      var chip = make('label', 'ck-addons__chip');
      var r = radio('ck_opt_' + idx, val);
      if (picked[idx] === val) { r.checked = true; chip.classList.add('is-selected'); }
      chip.appendChild(r);
      var sw = addon.swatches && addon.swatches[val];
      if (sw) { var dot = make('span', 'ck-addons__dot'); dot.style.background = sw; chip.appendChild(dot); }
      chip.appendChild(make('span', null, val));
      wrap.appendChild(chip);
    });
    fs.appendChild(wrap);
    return fs;
  }

  // The groups after option `from` are rebuilt from the picks before them; everything before,
  // the text field included, stays mounted, so the control the customer is on keeps focus.
  function rebuildAfter(form, addon, from) {
    var zone = form.querySelector('[data-ck-fields]');
    zone.querySelectorAll('[data-ck-opt]').forEach(function (fs) { if (Number(fs.getAttribute('data-ck-opt')) > from) fs.remove(); });
    var picked = pickedValues(form, addon);
    for (var idx = from + 1; idx < addon.options.length; idx++) zone.appendChild(optionGroup(addon, idx, picked));
    paintPrice(form, addon);
  }

  // The add-on's text field and its options. `picked` holds the values to preselect, null where open.
  function renderFields(form, addon, text, picked) {
    var zone = form.querySelector('[data-ck-fields]');
    var copy = form.querySelector('[data-ck-copy]');
    var instr = form.querySelector('[data-ck-instr]');
    var notice = form.querySelector('[data-ck-notice]');
    zone.innerHTML = '';
    // A chooser shows each member's description in its row; under the title it would repeat and move the form.
    copy.textContent = addon && form.querySelector('[data-ck-members]').hidden ? addon.description || '' : '';
    copy.hidden = !copy.textContent;
    instr.textContent = addon ? instructionFor(addon) : '';
    instr.hidden = !instr.textContent;
    notice.textContent = (addon && addon.notice) || '';
    notice.hidden = !notice.textContent;
    if (!addon) { paintPrice(form, null); return; }
    if (addon.text) {
      var lab = make('label', 'ck-addons__field');
      lab.appendChild(make('span', 'ck-addons__label', addon.text.label));
      lab.appendChild(Object.assign(make('input', 'ck-addons__text'), {
        type: 'text', name: 'ck_text', required: true, maxLength: addon.text.max, pattern: TEXT_PATTERN, autocomplete: 'off', value: text || ''
      }));
      zone.appendChild(lab);
    }
    addon.options.forEach(function (_, idx) { zone.appendChild(optionGroup(addon, idx, picked)); });
    paintPrice(form, addon);
  }

  function openDialog(card) {
    var list = eligible(card);
    // The card's current choice, if any; absent attributes read as null.
    var state = {
      member: card.getAttribute('data-ck-addon-member'),
      id: card.getAttribute('data-ck-addon-id'),
      text: card.getAttribute('data-ck-addon-text')
    };
    teardown();
    activeCard = card;
    var dlg = document.querySelector('template[data-ck-addons-dialog]').content.firstElementChild.cloneNode(true);
    var form = dlg.querySelector('form');
    dlg.querySelector('[data-ck-title]').textContent = list.length === 1 ? list[0].title : card.querySelector('.ck-addons__name').textContent;
    dlg.querySelector('[data-ck-remove]').hidden = !state.id;
    dlg.addEventListener('close', function () { if (activeDialog === dlg) teardown(); });
    document.body.appendChild(dlg);
    activeDialog = dlg;
    renderMembers(form, list, state);
    var addon = currentAddon(form, list);
    var chosen = addon && state.id ? variantById(addon, state.id) : null;
    renderFields(form, addon, state.text, chosen ? chosen.options : []);
    dlg.showModal();
  }

  // Writes the dialog's choice onto the card. False when no available variant matches.
  function applySelect(form) {
    var card = activeCard;
    var list = eligible(card);
    var addon = currentAddon(form, list);
    var v = variantFor(addon, pickedValues(form, addon));
    if (!v || !v.available) return false;
    var input = form.querySelector('input[name="ck_text"]');
    var text = input ? input.value : '';
    card.setAttribute('aria-pressed', 'true');
    card.setAttribute('data-ck-addon-member', addon.handle);
    card.setAttribute('data-ck-addon-id', v.id);
    card.setAttribute('data-ck-addon-price', v.price);
    card.removeAttribute('data-ck-addon-text');
    card.removeAttribute('data-ck-addon-text-label');
    if (text) {
      card.setAttribute('data-ck-addon-text', text);
      card.setAttribute('data-ck-addon-text-label', addon.text.label);
    }
    card.querySelector('.ck-addons__price').textContent = v.price_text;
    paintImage(card, v.image || addon.image);
    var bits = [];
    if (list.length > 1) bits.push(addon.title);
    if (v.options[0] !== 'Default Title') bits.push(v.options.join(' / '));
    if (text) bits.push('“' + text + '”');
    var sub = card.querySelector('.ck-addons__sub');
    sub.textContent = bits.join(' · ');
    sub.hidden = !bits.length;
    fire();
    return true;
  }

  /* ---------- events ---------- */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t === activeDialog || t.closest('[data-ck-cancel]')) { teardown(); return; }
    if (t.closest('[data-ck-remove]')) {
      var card = activeCard;
      teardown();
      resetCard(card);
      fire();
      return;
    }
    var opened = t.closest('[data-ck-addons] .ck-addons__card');
    if (opened) openDialog(opened);
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('.ck-addons__form')) return;
    e.preventDefault();
    var text = form.querySelector('input[name="ck_text"]');
    if (text) text.value = text.value.trim(); // spaces alone are no message; the trimmed text is what reaches the cart
    if (!form.checkValidity()) showError(form, firstProblem(form));
    else if (!applySelect(form)) showError(form, 'This combination is not available.');
    else teardown();
  });

  document.addEventListener('input', function (e) {
    var form = e.target.closest('.ck-addons__form');
    if (form) showError(form, '');
  });

  document.addEventListener('change', function (e) {
    var form = e.target.closest('.ck-addons__form');
    if (!form) return;
    showError(form, '');
    var list = eligible(activeCard);
    var text = form.querySelector('input[name="ck_text"]');
    if (e.target.name === 'ck_member') {
      form.querySelectorAll('.ck-addons__opt').forEach(function (o) { o.classList.toggle('is-selected', o.contains(e.target)); });
      renderFields(form, currentAddon(form, list), text && text.value, []);
    } else if (e.target.name.indexOf('ck_opt_') === 0) {
      // A change at option j keeps the picks before it and reopens the ones after it.
      var j = Number(e.target.name.slice(7));
      e.target.closest('[data-ck-opt]').querySelectorAll('.ck-addons__chip').forEach(function (c) { c.classList.toggle('is-selected', c.contains(e.target)); });
      rebuildAfter(form, currentAddon(form, list), j);
    }
  });

  document.addEventListener('ck:vehicle-changed', applyVehicle);
  document.addEventListener('shopify:section:load', applyVehicle);
  // The theme editor re-renders a section by unloading it: an open dialog belongs to the old block.
  document.addEventListener('shopify:section:unload', function (e) { if (activeCard && e.target.contains(activeCard)) teardown(); });
  applyVehicle();
})();
